const CrudDoc = () => {
  return (
    <section className="text-slate-300 space-y-10">
      {/* TITLE */}
      <div>
        <h2 className="text-3xl font-bold text-white mb-4">
          AxisNet CRUD — Feature & Controller Documentation
        </h2>
        <p>
          AxisNet CRUD provides a high-level abstraction layer built around
          MediatR, behavior-based pipelines, and attribute-driven automation.
          Each Feature (Command/Query) corresponds directly to an abstract
          controller endpoint. This document describes what each Feature does
          and the role of its corresponding endpoint within the CRUD and
          authentication ecosystem.
        </p>
      </div>

      {/* AUTH CONTROLLER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          🔐 AxisNetAuthController — Authentication & Identity Flows
        </h3>

        <p>
          This controller defines all authentication-related endpoints,
          including token generation, 2FA verification, refresh-token logic,
          logout flow, and the entire password-reset lifecycle.
        </p>

        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>
            <strong>GetAccessToken</strong> — Issues an Access Token using user
            credentials. Validates username/email + password and initializes a
            session.
          </li>

          <li>
            <strong>GetTwoFactorAccessToken</strong> — Completes the 2FA flow by
            validating the verification code and issuing new tokens upon
            success.
          </li>

          <li>
            <strong>RefreshToken</strong> — Uses a valid Refresh Token to
            generate a new Access Token without requiring a password or 2FA flow
            again.
          </li>

          <li>
            <strong>Logout</strong> — Terminates the active session, invalidates
            tokens, and prevents further authenticated actions.
          </li>

          <li>
            <strong>ForgotPassword</strong> — Initiates the password reset
            process by generating a secure verification code and sending it via
            email. Includes expiration/attempt-limiting policies.
          </li>

          <li>
            <strong>ForgotPasswordConfirm</strong> — Validates the reset code,
            ensures it hasn’t expired or been reused, updates the password, and
            marks the code as consumed.
          </li>
        </ul>
      </div>

      {/* LOG CONTROLLER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-blue-400 mb-3">
          📜 AxisNetLogController — Activity & System Logs
        </h3>

        <p>
          Provides multiple log retrieval endpoints for auditing and system
          monitoring.
        </p>

        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>
            <strong>Get(id)</strong> — Fetches a single log entry by its unique
            identifier.
          </li>

          <li>
            <strong>Get(pageRequest)</strong> — Returns all logs in paginated
            format, useful for dashboards and audit pages.
          </li>

          <li>
            <strong>Get(userId, pageRequest)</strong> — Retrieves all logs
            belonging to a specific user.
          </li>
        </ul>
      </div>

      {/* PERMISSION CONTROLLER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-yellow-400 mb-3">
          🛡️ AxisNetPermissionController — Permission Registry
        </h3>
        <p>
          Provides a paginated list of all registered system permissions. This
          endpoint is typically used by admin panels or role-permission
          management screens.
        </p>
      </div>

      {/* ROLE CONTROLLER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-purple-400 mb-3">
          👥 AxisNetRoleController — Role Registry
        </h3>
        <p>
          Retrieves all system roles with pagination. Often consumed by
          user-role assignment screens and administrative dashboards.
        </p>
      </div>

      {/* SESSION CONTROLLER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-cyan-400 mb-3">
          💠 AxisNetSessionController — User Sessions
        </h3>

        <p>
          Exposes endpoints for retrieving information related to user sessions.
          Sessions are used to track login devices, token sets, and active
          clients.
        </p>

        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>
            <strong>Get(id)</strong> — Retrieves full details of a specific
            session.
          </li>
          <li>
            <strong>Get(pageRequest)</strong> — Returns all sessions in
            paginated form.
          </li>
          <li>
            <strong>Get(userId, pageRequest)</strong> — Lists sessions that
            belong to a specific user.
          </li>
        </ul>
      </div>

      {/* USER PERMISSION CONTROLLER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-orange-400 mb-3">
          🔧 AxisNetUserPermissionController — User ↔ Permission Mapping
        </h3>

        <p>Manages the relationship between Users and Permissions.</p>

        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>
            <strong>GetUsersByPermissionId</strong> — Lists all users who
            possess a specific permission.
          </li>

          <li>
            <strong>GetPermissionsByUserId</strong> — Lists all permissions
            granted to a specific user.
          </li>

          <li>
            <strong>Post</strong> — Assigns a new permission to the user.
          </li>

          <li>
            <strong>Put</strong> — Updates an existing user-permission mapping.
          </li>

          <li>
            <strong>Delete</strong> — Removes a permission from the user.
          </li>
        </ul>
      </div>

      {/* USER ROLE CONTROLLER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-pink-400 mb-3">
          🎭 AxisNetUserRolesController — User ↔ Role Mapping
        </h3>

        <p>Controls the assignment of roles to users.</p>

        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>
            <strong>GetUsersByRoleId</strong> — Lists all users who hold a given
            role.
          </li>
          <li>
            <strong>GetRolesByUserId</strong> — Lists all roles assigned to a
            user.
          </li>
          <li>
            <strong>Post</strong> — Assigns a new role to a user.
          </li>
          <li>
            <strong>Put</strong> — Updates an existing user-role assignment.
          </li>
          <li>
            <strong>Delete</strong> — Removes a role from a user.
          </li>
        </ul>
      </div>

      {/* USERS CONTROLLER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          👤 AxisNetUsersController — Core User Management
        </h3>

        <p>
          Handles all CRUD and identity-related operations for users, including
          account creation, updates, deletion, email verification, password
          updates, and email-change workflows.
        </p>

        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>
            <strong>Get(id)</strong> — Retrieves a specific user by GUID.
          </li>
          <li>
            <strong>Get(withDeleted, pageRequest)</strong> — Paginated user list
            with soft-delete visibility.
          </li>
          <li>
            <strong>Get(withDeleted, pageRequest, filterRequest)</strong> — Adds
            dynamic filtering support.
          </li>

          <li>
            <strong>Post</strong> — Creates a new user.
          </li>
          <li>
            <strong>Put(UpdateUser)</strong> — Updates general user information.
          </li>
          <li>
            <strong>Put(UpdatePassword)</strong> — Updates the user's password.
          </li>

          <li>
            <strong>VerifyEmail</strong> — Sends verification email to the user.
          </li>
          <li>
            <strong>VerifyEmailConfirm</strong> — Confirms email using the
            verification code.
          </li>

          <li>
            <strong>ChangeEmail</strong> — Starts the email-change flow.
          </li>
          <li>
            <strong>ChangeEmailConfirm</strong> — Confirms the new email using a
            verification token.
          </li>

          <li>
            <strong>Delete</strong> — Deletes a user (soft or hard).
          </li>
        </ul>
      </div>
    </section>
  );
};

export default CrudDoc;
